import { startOfDay, endOfDay } from 'date-fns';

import {
  FILTER_FIELDS_TYPES,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';

const computeFieldType = (fieldType, value, field) => {
  if (fieldType === FILTER_FIELDS_TYPES.Boolean) {
    return value;
  }
  if (
    fieldType === FILTER_FIELDS_TYPES.String ||
    fieldType === FILTER_FIELDS_TYPES.Phone ||
    fieldType === FILTER_FIELDS_TYPES.Email
  ) {
    return Array.isArray(value)
      ? value?.map((item) => {
          return `${item}`;
        })
      : `${value}`;
  }
  if (fieldType === FILTER_FIELDS_TYPES.Number) {
    return Array.isArray(value)
      ? value?.map((item) => {
          return Number(item);
        })
      : Number(value);
  }
  if (
    fieldType === FILTER_FIELDS_TYPES.Date ||
    fieldType === FILTER_FIELDS_TYPES.DateTime
  ) {
    return Array.isArray(value) && value?.length > 1
      ? value?.map((item) => {
          return new Date(item)?.getTime();
        })
      : new Date(value[0])?.getTime();
  }
  if (fieldType === FILTER_FIELDS_TYPES.Select) {
    if (Array.isArray(value)) {
      return value?.map((item) => {
        return field?.valueType === 'number' ? Number(item) : `${item}`;
      });
    }

    return field?.valueType === 'number' ? Number(value) : `${value}`;
  }
  if (fieldType === FILTER_FIELDS_TYPES.Autocomplete) {
    return field?.valueType === 'number'
      ? Number(value?.value)
      : `${value?.value}`;
  }
  if (fieldType === FILTER_FIELDS_TYPES.MultiSelect) {
    return value?.map((item) => {
      return field?.valueType === 'number'
        ? Number(item.value)
        : `${item.value}`;
    });
  }
};

export const computeValueByRuleType = (ruledType, fieldType, value, field) => {
  // TODO: add date between and string between
  if (ruledType === FILTER_RULE_ENUMS.AndRuleType) {
    return !value ? [] : computeFieldType(fieldType, value?.split(','), field);
  }

  if (
    ruledType === FILTER_RULE_ENUMS.ComparableRuleType ||
    ruledType === FILTER_RULE_ENUMS.ComparePriceRuleType ||
    ruledType === FILTER_RULE_ENUMS.CompareStringRuleType ||
    ruledType === FILTER_RULE_ENUMS.OrderedRuleType
  ) {
    return computeFieldType(fieldType, value, field);
  }
  if (
    ruledType === FILTER_RULE_ENUMS.CompareDateRuleType ||
    ruledType === FILTER_RULE_ENUMS.IsAnyOfRuleType ||
    ruledType === FILTER_RULE_ENUMS.DateBetween ||
    ruledType === FILTER_RULE_ENUMS.OrRuleType
  ) {
    return computeFieldType(
      fieldType,
      typeof value === 'string' ? value?.split(',') : value,
      field
    );
  }
  if (ruledType === FILTER_RULE_ENUMS.EmptyRuleType) {
    return null;
  }

  if (ruledType === FILTER_RULE_ENUMS.NotRuleType) {
    return value;
  }
};

const mapRuleToPayload = ({ fields, item }) => {
  // If the field type is date and operator is less than, less than or equal,
  // greater than or greater than or equal, we need to adjust the value
  // to include the time for correct filtering on backend
  const isDateType =
    fields?.[item?.field]?.type === FILTER_FIELDS_TYPES.Date ||
    fields?.[item?.field]?.type === FILTER_FIELDS_TYPES.DateTime;

  if (
    isDateType &&
    [
      FILTER_OPERATOR_ENUMS['<'],
      FILTER_OPERATOR_ENUMS['<='],
      FILTER_OPERATOR_ENUMS['>'],
      FILTER_OPERATOR_ENUMS['>='],
    ].includes(FILTER_OPERATOR_ENUMS[item?.operator])
  ) {
    const date = new Date(item?.value);
    const isLessThan =
      FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['<'];
    const isLessThanOrEqual =
      FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['<='];
    const boundaryDate =
      isLessThan || isLessThanOrEqual
        ? (isLessThan ? startOfDay(date) : endOfDay(date)).toISOString()
        : (FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['>']
            ? endOfDay(date)
            : startOfDay(date)
          ).toISOString();

    return {
      type: fields?.[item?.field]?.ruleType,
      field: item?.field,
      operator: FILTER_OPERATOR_ENUMS[item?.operator],
      value: computeValueByRuleType(
        fields?.[item?.field]?.ruleType,
        fields?.[item?.field]?.type,
        boundaryDate,
        fields?.[item?.field]
      ),
    };
  }

  if (
    (isDateType &&
      FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['=']) ||
    (isDateType &&
      FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['!='])
  ) {
    const date = new Date(item?.value);
    const dt = startOfDay(date).toISOString();
    const de = endOfDay(date).toISOString();

    return {
      type:
        FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['=']
          ? FILTER_RULE_ENUMS.AndRuleType
          : FILTER_RULE_ENUMS.OrRuleType,
      field: null,
      operator: null,
      value: [
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: item?.field,
          operator:
            FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['=']
              ? FILTER_OPERATOR_ENUMS['>=']
              : FILTER_OPERATOR_ENUMS['<'],
          value: computeValueByRuleType(
            fields?.[item?.field]?.ruleType,
            fields?.[item?.field]?.type,
            dt,
            fields?.[item?.field]
          ),
        },
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: item?.field,
          operator:
            FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS['=']
              ? FILTER_OPERATOR_ENUMS['<=']
              : FILTER_OPERATOR_ENUMS['>'],
          value: computeValueByRuleType(
            fields?.[item?.field]?.ruleType,
            fields?.[item?.field]?.type,
            de,
            fields?.[item?.field]
          ),
        },
      ],
    };
  }

  if (
    isDateType &&
    FILTER_OPERATOR_ENUMS[item?.operator] === FILTER_OPERATOR_ENUMS.between
  ) {
    let startDate;
    let endDate;
    const dates = item?.value?.split(',');

    if (!dates?.[1]) {
      startDate = startOfDay(new Date(dates[0])).toISOString();
      endDate = endOfDay(new Date(dates[0])).toISOString();
    } else {
      startDate = startOfDay(new Date(dates[0])).toISOString();
      endDate = endOfDay(new Date(dates[1])).toISOString();
    }

    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: [
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: item?.field,
          operator: FILTER_OPERATOR_ENUMS['>='],
          value: computeValueByRuleType(
            fields?.[item?.field]?.ruleType,
            fields?.[item?.field]?.type,
            startDate,
            fields?.[item?.field]
          ),
        },
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: item?.field,
          operator: FILTER_OPERATOR_ENUMS['<='],
          value: computeValueByRuleType(
            fields?.[item?.field]?.ruleType,
            fields?.[item?.field]?.type,
            endDate,
            fields?.[item?.field]
          ),
        },
      ],
    };
  }

  return {
    type: fields?.[item?.field]?.ruleType,
    field: item?.field,
    operator: FILTER_OPERATOR_ENUMS[item?.operator],
    value: computeValueByRuleType(
      fields?.[item?.field]?.ruleType,
      fields?.[item?.field]?.type,
      item?.value,
      fields?.[item?.field]
    ),
  };
};

export const getPayload = ({ fields, query }) => {
  if (query?.andQuery || query?.orQuery) {
    const andRules = query?.andQuery?.rules || [];
    const orRules = query?.orQuery?.rules || [];

    const andValue = andRules.map((item) => mapRuleToPayload({ fields, item }));
    const orValue = orRules.map((item) => mapRuleToPayload({ fields, item }));

    if (orValue.length) {
      andValue.push({
        type: FILTER_RULE_ENUMS.OrRuleType,
        field: null,
        operator: null,
        value: orValue,
      });
    }

    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: andValue,
    };
  }

  const combinator = query?.combinator;
  const rootRuleType =
    typeof combinator === 'string' && combinator.toLowerCase() === 'or'
      ? FILTER_RULE_ENUMS.OrRuleType
      : FILTER_RULE_ENUMS.AndRuleType;

  const payload = {
    type: rootRuleType,
    field: null,
    operator: null,
    value: query?.rules?.map((item) => mapRuleToPayload({ fields, item })),
  };
  return payload;
};
